import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { CollapsiblePanel } from '../../../../../../components/CarFinancePage/components/Panels/shared/CollapsiblePanel/CollapsiblePanel';
import { CollapsiblePanelProvider } from '../../../../../../components/CarFinancePage/components/Panels/shared/CollapsiblePanel/CollapsiblePanel.context';
import { Label } from '../../../../../../components/Toolkit/Inputs/Label';
import { FinanceTextField } from '../../../../../../components/CarFinancePage/financeProviders/shared/FinanceTextField/FinanceTextField';
import { FinanceSelect } from '../../../../../../components/CarFinancePage/financeProviders/shared/FinanceSelect';
import { Spacer } from '../../../../../../helpers/genericStyles';
import { Divider } from '../../../../../../components/ToolkitV2/Divider/Divider';

import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_BOI } from './constants';

import { type BankOfIrelandForm } from '../../../../../../components/CarFinancePage/queries/schema/carFinance.schema';
const RESIDENTIAL_STATUS = [
  { label: 'Select status', value: '' },
  'Single (With Parents)',
  'Single Tenant',
  'Single Homeowner',
  'Married/Couple Tenant',
  'Married/Couple Homeowner',
];
const COUNTIES = [
  { label: 'Select county', value: '' },
  'Carlow',
  'Cavan',
  'Clare',
  'Cork',
  'Donegal',
  'Dublin 1',
  'Dublin 2',
  'Dublin 3',
  'Dublin 4',
  'Dublin 5',
  'Dublin 6',
  'Dublin 6W',
  'Dublin 7',
  'Dublin 8',
  'Dublin 9',
  'Dublin 10',
  'Dublin 11',
  'Dublin 12',
  'Dublin 13',
  'Dublin 14',
  'Dublin 15',
  'Dublin 16',
  'Dublin 17',
  'Dublin 18',
  'Dublin 20',
  'Dublin 23',
  'Dublin 24',
  'Dublin',
  'Galway',
  'Kerry',
  'Kildare',
  'Kilkenny',
  'Laois',
  'Leitrim',
  'Limerick',
  'Longford',
  'Louth',
  'Mayo',
  'Meath',
  'Monaghan',
  'Offaly',
  'Roscommon',
  'Sligo',
  'Tipperary',
  'Waterford',
  'Westmeath',
  'Wexford',
  'Wicklow',
];
const TIME_AT_ADDRESS_YEARS = [
  { value: '', label: 'Select time' },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: '6', label: 'More than 5 years' },
];

const ResidentialDetails = () => {
  const [showPreviousAddressFields, setShowPreviousAddressFields] =
    useState(false);
  const { control, resetField } = useFormContext<BankOfIrelandForm>();

  const handleTimeAtCurrentAddressChange = (selected: string) => {
    if (
      Number(selected) > THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_BOI
    ) {
      setShowPreviousAddressFields(false);
      resetField('previousAddress');
    } else {
      setShowPreviousAddressFields(true);
    }
  };

  return (
    <CollapsiblePanelProvider panelName="currentAddress">
      <CollapsiblePanel
        headerText="Residential Details"
        accordionName="Residential Details"
        mb={['S8', 'M16']}
        panelName="currentAddress"
      >
        <Spacer marginBottom={['M16', 'M24']} />
        <Label htmlFor="currentAddress.residentialStatus">
          Residential Status*
        </Label>
        <Controller
          control={control}
          name="currentAddress.residentialStatus"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={RESIDENTIAL_STATUS}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.addressLine1">
          Current Address Line 1*
        </Label>
        <Controller
          control={control}
          name="currentAddress.addressLine1"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter current address line 1"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.addressLine2">
          Current Address Line 2*
        </Label>
        <Controller
          control={control}
          name="currentAddress.addressLine2"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter address line 2"
              isOptional
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.addressLine3">
          Current Address Line 3
        </Label>
        <Controller
          control={control}
          name="currentAddress.addressLine3"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter address line 3"
              isOptional
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.county">County*</Label>
        <Controller
          control={control}
          name="currentAddress.county"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={COUNTIES}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="currentAddress.timeAtCurrentAddress.years">
          Time at Current Address*
        </Label>
        <Controller
          control={control}
          name="currentAddress.timeAtCurrentAddress.years"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              onChange={(selected) => {
                field.onChange(selected);
                handleTimeAtCurrentAddressChange(selected);
              }}
              placeholderAsOption
              options={TIME_AT_ADDRESS_YEARS}
            />
          )}
        />
        {showPreviousAddressFields && (
          <>
            <Spacer marginBottom={'S12'} />
            <Divider />
            <Spacer marginBottom={'M32'} />
            <Label htmlFor="previousAddress.addressLine1">
              Prevous Address Line 1*
            </Label>
            <Controller
              control={control}
              name="previousAddress.addressLine1"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter address line 1"
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.addressLine2">
              Prevous Address Line 2
            </Label>
            <Controller
              control={control}
              name="previousAddress.addressLine2"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter address line 2"
                  isOptional
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.addressLine3">
              Prevous Address Line 3
            </Label>
            <Controller
              control={control}
              name="previousAddress.addressLine3"
              render={({ field, fieldState }) => (
                <FinanceTextField
                  {...field}
                  {...fieldState}
                  placeholder="Enter address line 3"
                  isOptional
                />
              )}
            />
            <Spacer marginBottom="S4" />
            <Label htmlFor="previousAddress.timeAtAddress.county">
              Prevous County
            </Label>
            <Controller
              control={control}
              name="previousAddress.county"
              render={({ field, fieldState }) => (
                <FinanceSelect
                  {...field}
                  {...fieldState}
                  placeholderAsOption
                  options={COUNTIES}
                />
              )}
            />
          </>
        )}
      </CollapsiblePanel>
    </CollapsiblePanelProvider>
  );
};

export { ResidentialDetails };
